import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GoogleIcon from "@mui/icons-material/Google";
import axios from "axios";
import { Link } from "react-router-dom";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { message } from "../Constants/message.js";
import GoogleKeywordSuggestContent from "../Contents/GoogleKeywordSuggestionContent.js";
import CommentSection from "../Components/CommentSection.js";

function GoogleKeywordSuggestion({ user, login, isLoggedIn }) {
  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortByDifficulty, setSortByDifficulty] = useState(false);
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const storedKeyword = localStorage.getItem("primaryInputText");
    if (storedKeyword) {
      setKeyword(storedKeyword);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const getDifficultyScore = (kw) => {
    const len = kw.length;
    const wordCount = kw.split(/\s+/).filter(Boolean).length;
    const score = Math.min(100, Math.floor(len * 0.5 + wordCount * 5));
    let finalScore = 100 - score;
    return finalScore;
  };

  const handleKeywordChange = (e) => {
    if (e.target.value.length <= 120) setKeyword(e.target.value);
  };

  const handleGenerateSuggestions = async () => {
    setSuccess("");
    setError("");

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => login(), 1500);
      return;
    }

    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) return setError("Please enter a keyword.");
    if (trimmedKeyword.length > 120)
      return setError("Keyword must be 120 characters or fewer.");
    if (credits.remaining <= 0)
      return setError("Not enough credits to see suggested keywords.");

    setLoading(true);
    setSuggestions([]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/google-keyword-suggestion`,
        { google_search_query: keyword },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const rawKeywords = response.data.keywords || [];
      const parsed = rawKeywords.map((k) => ({
        keyword: k,
        difficulty: getDifficultyScore(k),
      }));

      setSuggestions(parsed);
      setSuccess(response.data.successMessage || "Suggestions generated!");

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to fetch suggestions. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const sortedSuggestions = [...suggestions].sort((a, b) =>
    sortByDifficulty
      ? sortOrder === "asc"
        ? a.difficulty - b.difficulty
        : b.difficulty - a.difficulty
      : 0
  );

  const toggleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setSortByDifficulty(true);
  };

  const copyTableToClipboard = () => {
    const header = ["Keyword", "Seed Keyword", "Source", "Difficulty"];
    const rows = suggestions.map((tag) => [
      tag.keyword,
      keyword,
      "Google",
      tag.difficulty,
    ]);

    const content = [header, ...rows].map((row) => row.join("\t")).join("\n");
    navigator.clipboard.writeText(content);
    setSuccess("Table copied to clipboard!");
  };

  const downloadCSV = () => {
    const header = ["Keyword", "Seed Keyword", "Source", "Difficulty"];
    const rows = suggestions.map((tag) => [
      tag.keyword,
      keyword,
      "Google",
      tag.difficulty,
    ]);
    const csv = [header, ...rows].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "keyword_suggestions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Google Keyword Suggestions 💡
          </TitleText>
          <SubtitleText variant="subtitle1" align="center">
            Enter a keyword and get auto suggestions from Google.
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Enter Seed Keyword"
              variant="outlined"
              value={keyword}
              onChange={handleKeywordChange}
              placeholder="e.g. fitness, digital marketing, travel"
              size="medium"
              inputProps={{ maxLength: 120 }}
              helperText={`${keyword.length}/120 characters`}
               name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleGenerateSuggestions}
            >
              Generate Suggestions
            </StyledButton>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b>💡 1 Search keyword = 1 Credit</b>
              <br />
              Have questions? <Link to="/contact">Contact support</Link>
            </Typography>
          </Box>

          {suggestions.length > 0 && (
            <Box mt={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body1" fontWeight="bold">
                  Suggested Keywords:
                </Typography>
                <Box>
                  <IconButton
                    onClick={copyTableToClipboard}
                    size="small"
                    title="Copy table"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    onClick={downloadCSV}
                    size="small"
                    title="Download CSV"
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </Box>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Keyword</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Seed Keyword</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Source</strong>
                      </TableCell>
                      <TableCell align="center" sortDirection={sortOrder}>
                        <TableSortLabel
                          active={sortByDifficulty}
                          direction={sortOrder}
                          onClick={toggleSort}
                        >
                          <strong>Keyword Difficulty</strong>
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSuggestions.map((tag, i) => (
                      <TableRow key={i}>
                        <TableCell>{tag.keyword}</TableCell>
                        <TableCell>{keyword}</TableCell>
                        <TableCell>
                          <GoogleIcon color="primary" />
                        </TableCell>
                        <TableCell align="center">{tag.difficulty}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </ContentBox>
        <CommentSection toolId="google-keyword-auto-suggestion" user={user} login={login} isLoggedIn={isLoggedIn}/>
        <GoogleKeywordSuggestContent />
      </PageContainer>
    </>
  );
}

export default GoogleKeywordSuggestion;
